import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckSquare,
  ExternalLink,
  FileText,
  GitCommit,
  Loader2,
  Pencil,
  Plus,
  Send,
  Sparkles,
  Trash2,
  Undo2,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";

import { PageContainer } from "@/components/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  useAdminFeed,
  useCreaVoceFeedManuale,
  useGeneraAttivita,
  usePubblicaMolte,
  usePubblicaVoceFeed,
  useRipristinaMolte,
  useScartaMolte,
  useScartaVoceFeed,
  useUpdateVoceFeed,
} from "@/hooks/useAdminFeed";
import { useMe } from "@/hooks/useMe";
import { useModuli } from "@/hooks/useModuli";
import { ApiError } from "@/lib/api";
import { isMaitimeStaff } from "@/lib/nav-config";
import { cn } from "@/lib/utils";
import type {
  FeedStato,
  FeedTipo,
  VoceFeedAdmin,
} from "@/types/api";

const TIPI: { value: FeedTipo; label: string }[] = [
  { value: "completato", label: "Completato" },
  { value: "in_corso", label: "In corso" },
  { value: "fix", label: "Fix" },
  { value: "dati", label: "Dati" },
  { value: "documenti", label: "Documenti" },
  { value: "infra", label: "Infra" },
  { value: "altro", label: "Altro" },
];

function tipoLabel(t: FeedTipo): string {
  return TIPI.find((x) => x.value === t)?.label ?? t;
}

function formatData(iso: string): string {
  return new Date(iso).toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Il tetto che la lista chiede al backend. Sopra questo, la pagina lo dice. */
const LIMITE = 200;

export function AdminFeedPage() {
  const { data: me, isLoading: loadingMe } = useMe();
  const [tabStato, setTabStato] = useState<FeedStato>("bozza");
  const { data: voci = [], isLoading } = useAdminFeed({
    stato: tabStato,
    limit: LIMITE,
  });
  const [editing, setEditing] = useState<VoceFeedAdmin | null>(null);
  const [manualeOpen, setManualeOpen] = useState(false);
  const [selezione, setSelezione] = useState<Set<string>>(new Set());

  // Cambiando tab la selezione non ha più senso: sono altre voci.
  function cambiaTab(v: string) {
    setTabStato(v as FeedStato);
    setSelezione(new Set());
  }

  function inverti(id: string) {
    setSelezione((prec) => {
      const next = new Set(prec);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /** Pubblicabile = ha già titolo e corpo. Le grezze non lo sono. */
  const pronte = useMemo(
    () => voci.filter((v) => v.titolo_pubblicato && v.corpo_pubblicato),
    [voci]
  );

  if (!loadingMe && me && !isMaitimeStaff(me)) {
    return <Navigate to="/" replace />;
  }

  return (
    <PageContainer>
      <Link
        to="/admin"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-3.5" />
        Backoffice
      </Link>

      <header className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Backoffice Maitime
          </p>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            Review feed commit
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl pt-1">
            Le bozze arrivano dal webhook GitHub (un push = N bozze). Riscrivile in italiano user-friendly, scegli tipo e modulo, poi <span className="font-medium">pubblica</span> per renderle visibili al cliente nella sezione Attività.
          </p>
          <p className="text-sm text-muted-foreground max-w-2xl">
            <span className="font-medium">Genera dai commit</span> fa il primo giro al
            posto tuo: raggruppa le bozze per giornata e fa scrivere una voce in lingua
            cliente. Restano <span className="font-medium">bozze</span> — si rileggono e
            si pubblicano da qui.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <GeneraButton />
          <Button variant="outline" onClick={() => setManualeOpen(true)}>
            <Plus className="size-4" />
            Voce manuale
          </Button>
        </div>
      </header>

      <Tabs value={tabStato} onValueChange={cambiaTab}>
        <TabsList>
          <TabsTrigger value="bozza">Bozze</TabsTrigger>
          <TabsTrigger value="pubblicato">Pubblicate</TabsTrigger>
          <TabsTrigger value="scartato">Scartate</TabsTrigger>
        </TabsList>
      </Tabs>

      {voci.length > 0 && (
        <BarraSelezione
          tab={tabStato}
          voci={voci}
          pronte={pronte}
          selezione={selezione}
          onSelezione={setSelezione}
        />
      )}

      {/* Il taglio non deve essere silenzioso: una lista troncata senza dirlo
          è come si perdono di vista dieci voci appena generate. */}
      {voci.length >= LIMITE && (
        <p className="text-xs text-warning inline-flex items-center gap-1.5">
          <AlertTriangle className="size-3.5" />
          Mostrate le prime {LIMITE}: ce ne sono altre. Smaltisci queste e ricarica.
        </p>
      )}

      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : voci.length === 0 ? (
        <div className="rounded-lg border bg-card/40 p-8 text-center text-sm text-muted-foreground">
          {tabStato === "bozza"
            ? "Nessuna bozza in attesa. Quando arriverà un push GitHub, le bozze appariranno qui."
            : tabStato === "pubblicato"
              ? "Nessuna voce pubblicata."
              : "Nessuna voce scartata."}
        </div>
      ) : (
        <div className="space-y-2">
          {voci.map((v) => (
            <VoceCard
              key={v.id}
              voce={v}
              onEdit={() => setEditing(v)}
              selezionabile
              selezionata={selezione.has(v.id)}
              onSeleziona={() => inverti(v.id)}
            />
          ))}
        </div>
      )}

      {editing && (
        <EditDialog voce={editing} onClose={() => setEditing(null)} />
      )}
      {manualeOpen && <ManualeDialog onClose={() => setManualeOpen(false)} />}
    </PageContainer>
  );
}

function ManualeDialog({ onClose }: { onClose: () => void }) {
  const create = useCreaVoceFeedManuale();
  const { data: moduli = [] } = useModuli();
  const [titolo, setTitolo] = useState("");
  const [corpo, setCorpo] = useState("");
  const [tipo, setTipo] = useState<FeedTipo>("altro");
  const [moduloCodice, setModuloCodice] = useState("");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    create.mutate(
      {
        titolo: titolo.trim(),
        corpo: corpo.trim(),
        tipo,
        modulo_codice: moduloCodice || undefined,
      },
      { onSuccess: () => onClose() }
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Nuova voce manuale</DialogTitle>
          <DialogDescription>
            Voce di feed senza commit GitHub (eventi non-codice). Pubblicata direttamente sul portale cliente.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Titolo</label>
            <input
              type="text"
              value={titolo}
              onChange={(e) => setTitolo(e.target.value)}
              required
              maxLength={200}
              placeholder="Es: Ricevuti documenti export Bielorussia da Marina"
              className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Corpo</label>
            <Textarea
              value={corpo}
              onChange={(e) => setCorpo(e.target.value)}
              rows={4}
              required
              maxLength={4000}
              placeholder="Spiega in italiano cosa è successo e perché è rilevante per il progetto."
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium">Tipo</label>
              <select
                value={tipo}
                onChange={(e) => setTipo(e.target.value as FeedTipo)}
                className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {TIPI.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium">Modulo collegato</label>
              <select
                value={moduloCodice}
                onChange={(e) => setModuloCodice(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">— Nessuno —</option>
                {moduli.map((m) => (
                  <option key={m.codice} value={m.codice}>
                    {m.codice} · {m.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {create.error instanceof ApiError && (
            <div className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-xs text-destructive">
              {create.error.message}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={create.isPending || !titolo.trim() || !corpo.trim()}
            >
              {create.isPending && <Loader2 className="size-3.5 animate-spin" />}
              Pubblica subito
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Card voce ────────────────────────────────────────────────────────

function VoceCard({
  voce: v,
  onEdit,
  selezionabile,
  selezionata,
  onSeleziona,
}: {
  voce: VoceFeedAdmin;
  onEdit: () => void;
  selezionabile: boolean;
  selezionata: boolean;
  onSeleziona: () => void;
}) {
  const pubblica = usePubblicaVoceFeed();
  const scarta = useScartaVoceFeed();

  const isPending = pubblica.isPending || scarta.isPending;
  const isBozza = v.stato === "bozza";
  const isPubblicato = v.stato === "pubblicato";

  function handlePubblica() {
    if (!v.titolo_pubblicato || !v.corpo_pubblicato) {
      alert("Per pubblicare servono sia titolo sia corpo. Apri la modifica.");
      return;
    }
    pubblica.mutate(v.id);
  }

  function handleScarta() {
    if (!confirm("Scartare questa voce? Resterà in DB per audit ma non sarà mai pubblicata.")) {
      return;
    }
    scarta.mutate(v.id);
  }

  const pronta = Boolean(v.titolo_pubblicato && v.corpo_pubblicato);

  return (
    <div
      className={cn(
        "rounded-lg border bg-card p-4 transition-colors",
        isPending && "opacity-50",
        selezionata && "border-primary/50 bg-primary/5"
      )}
    >
      <div className="flex items-start justify-between gap-4">
        {selezionabile && (
          <input
            type="checkbox"
            checked={selezionata}
            onChange={onSeleziona}
            // La spunta resta attiva su tutte: le grezze non si pubblicano
            // (manca titolo o corpo) ma si scartano, e le scartate si riportano
            // in bozza. Il pulsante giusto lo sceglie la barra in base alla scheda.
            aria-label={`Seleziona ${v.titolo_pubblicato ?? v.git_sha.substring(0, 8)}`}
            title={
              v.stato !== "bozza"
                ? "Selezionabile per riportarla in bozza"
                : pronta
                  ? "Pronta da pubblicare"
                  : "Grezza: prima serve un titolo, o si scarta"
            }
            className="size-4 mt-1 shrink-0"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
            <GitCommit className="size-3.5" />
            <span className="font-mono">{v.git_sha.substring(0, 8)}</span>
            <span>·</span>
            <span>{v.git_autore}</span>
            <span>·</span>
            <span>{formatData(v.git_committed_at)}</span>
            {v.git_branch && (
              <>
                <span>·</span>
                <Badge variant="outline" className="text-[10px]">
                  {v.git_branch}
                </Badge>
              </>
            )}
            {v.git_url && (
              <a
                href={v.git_url}
                target="_blank"
                rel="noreferrer"
                className="text-muted-foreground hover:text-foreground"
              >
                <ExternalLink className="size-3" />
              </a>
            )}
          </div>

          {v.titolo_pubblicato ? (
            <h3 className="font-semibold text-foreground">{v.titolo_pubblicato}</h3>
          ) : (
            <h3 className="font-semibold text-muted-foreground italic">
              (titolo non ancora riscritto)
            </h3>
          )}

          {v.corpo_pubblicato ? (
            <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">
              {v.corpo_pubblicato}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground/70 mt-1">
              <span className="font-medium">Messaggio commit:</span>{" "}
              <span className="font-mono">{v.git_messaggio_originale.split("\n")[0]}</span>
            </p>
          )}

          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge variant="outline" className="text-[10px]">
              tipo: {tipoLabel(v.tipo)}
            </Badge>
            {v.modulo_id && <ModuloBadge moduloId={v.modulo_id} />}
            {v.files_modificati && v.files_modificati.length > 0 && (
              <span className="text-[10px] text-muted-foreground">
                <FileText className="size-3 inline mr-0.5" />
                {v.files_modificati.length} file
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <StatoBadge stato={v.stato} />
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={onEdit} disabled={isPending}>
              <Pencil className="size-3.5" />
            </Button>
            {isBozza && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handlePubblica}
                  disabled={isPending || !v.titolo_pubblicato || !v.corpo_pubblicato}
                  title={
                    !v.titolo_pubblicato || !v.corpo_pubblicato
                      ? "Manca titolo o corpo"
                      : "Pubblica"
                  }
                >
                  {pubblica.isPending ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <Send className="size-3.5 text-success" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleScarta}
                  disabled={isPending}
                >
                  {scarta.isPending ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="size-3.5 text-destructive" />
                  )}
                </Button>
              </>
            )}
            {isPubblicato && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleScarta}
                disabled={isPending}
                title="Ritira (scarta)"
              >
                <Trash2 className="size-3.5 text-destructive" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatoBadge({ stato }: { stato: FeedStato }) {
  if (stato === "pubblicato") {
    return (
      <Badge
        variant="outline"
        className="text-[10px] bg-success/10 text-success border-success/30"
      >
        <Check className="size-3 mr-1" /> pubblicato
      </Badge>
    );
  }
  if (stato === "scartato") {
    return (
      <Badge variant="outline" className="text-[10px] bg-muted text-muted-foreground">
        <X className="size-3 mr-1" /> scartato
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="text-[10px] bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800"
    >
      bozza
    </Badge>
  );
}

function ModuloBadge({ moduloId }: { moduloId: string }) {
  const { data: moduli = [] } = useModuli();
  const m = moduli.find((x) => x.id === moduloId);
  if (!m) return null;
  return (
    <Badge variant="outline" className="text-[10px]">
      {m.codice} · {m.nome}
    </Badge>
  );
}

// ── Dialog modifica ──────────────────────────────────────────────────

function EditDialog({
  voce,
  onClose,
}: {
  voce: VoceFeedAdmin;
  onClose: () => void;
}) {
  const update = useUpdateVoceFeed();
  const { data: moduli = [] } = useModuli();
  const [titolo, setTitolo] = useState(voce.titolo_pubblicato ?? "");
  const [corpo, setCorpo] = useState(voce.corpo_pubblicato ?? "");
  const [tipo, setTipo] = useState<FeedTipo>(voce.tipo);
  const [moduloCodice, setModuloCodice] = useState(
    voce.modulo_id ? (moduli.find((m) => m.id === voce.modulo_id)?.codice ?? "") : ""
  );

  // Quando carichiamo moduli dopo render iniziale, sync codice modulo.
  useMemo(() => {
    if (!voce.modulo_id) return;
    const m = moduli.find((x) => x.id === voce.modulo_id);
    if (m && m.codice !== moduloCodice) setModuloCodice(m.codice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduli.length]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    update.mutate(
      {
        id: voce.id,
        body: {
          titolo_pubblicato: titolo.trim() || undefined,
          corpo_pubblicato: corpo.trim() || null,
          tipo,
          modulo_codice: moduloCodice,
        },
      },
      { onSuccess: () => onClose() }
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Riscrivi voce feed</DialogTitle>
          <DialogDescription>
            Messaggio originale commit:{" "}
            <span className="font-mono text-xs">{voce.git_messaggio_originale.split("\n")[0]}</span>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">
              Titolo (visibile cliente)
            </label>
            <input
              type="text"
              value={titolo}
              onChange={(e) => setTitolo(e.target.value)}
              maxLength={200}
              placeholder="Es: Pubblicata Sezione Documenti del progetto"
              className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">
              Corpo (visibile cliente, opzionale)
            </label>
            <Textarea
              value={corpo}
              onChange={(e) => setCorpo(e.target.value)}
              rows={4}
              placeholder="Spiega in italiano cosa significa questo cambiamento per Mister Pet."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">Tipo</label>
              <select
                value={tipo}
                onChange={(e) => setTipo(e.target.value as FeedTipo)}
                className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {TIPI.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">
                Modulo collegato
              </label>
              <select
                value={moduloCodice}
                onChange={(e) => setModuloCodice(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">— Nessuno —</option>
                {moduli.map((m) => (
                  <option key={m.codice} value={m.codice}>
                    {m.codice} · {m.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {update.error instanceof ApiError && (
            <div className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-xs text-destructive">
              {update.error.message}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Annulla
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending && <Loader2 className="size-3.5 animate-spin" />}
              Salva
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * «Genera dai commit» — il primo giro di scrittura fatto da Claude.
 *
 * Non pubblica: riempie titolo e corpo e lascia la voce in bozza. È la vetrina
 * del cliente, e un messaggio di commit può contenere roba interna o dire una
 * cosa che non vogliamo dire così. Un clic di conferma è sostenibile; una
 * figuraccia automatica no.
 *
 * ## Perché cicla
 *
 * Il backend lavora **a lotti di giornate** per non tenere aperta una richiesta
 * HTTP per minuti (con tre mesi di arretrato sarebbero decine di chiamate a
 * Claude in fila, e il browser o Cloud Run staccherebbero prima della fine).
 * Ma chi preme il pulsante vuole «falle tutte», non «fanne dieci»: quindi è la
 * pagina a richiamarsi finché `giornate_rimaste` non arriva a zero, mostrando
 * quante ne ha fatte man mano.
 */
function GeneraButton() {
  const genera = useGeneraAttivita();
  const [inCorso, setInCorso] = useState(false);
  const [fatte, setFatte] = useState(0);
  const [esito, setEsito] = useState<string | null>(null);

  /** Freno di sicurezza: se `giornate_rimaste` non scendesse mai (un lotto in
   *  cui ogni giornata viene saltata), il ciclo si fermerebbe comunque. */
  const MAX_GIRI = 40;

  async function generaTutte() {
    setInCorso(true);
    setEsito(null);
    setFatte(0);

    let totaleVoci = 0;
    let totaleCommit = 0;
    let totaleRumore = 0;
    let giri = 0;

    try {
      for (;;) {
        const r = await genera.mutateAsync({ giorni: 3650, max_giornate: 5 });
        totaleVoci += r.generate;
        totaleCommit += r.assorbite;
        totaleRumore += r.rumore_scartate;
        setFatte(totaleVoci);
        giri += 1;
        if (r.giornate_rimaste <= 0 || giri >= MAX_GIRI) break;
      }
      const coda =
        totaleRumore > 0 ? ` ${totaleRumore} merge/wip scartati.` : "";
      setEsito(
        totaleVoci === 0
          ? `Niente di nuovo da riassumere.${coda}`
          : `${totaleVoci} voci create da ${totaleCommit} commit, qui sotto in bozza.${coda}`
      );
    } catch (err) {
      setEsito(
        err instanceof ApiError
          ? `Generazione interrotta: ${err.message}`
          : "Generazione interrotta. Le voci già create restano in bozza."
      );
    } finally {
      setInCorso(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      {(esito || inCorso) && (
        <span className="text-xs text-muted-foreground max-w-[280px]">
          {inCorso ? `Generate ${fatte}…` : esito}
        </span>
      )}
      <Button variant="outline" disabled={inCorso} onClick={generaTutte}>
        {inCorso ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Sparkles className="size-4" />
        )}
        Genera dai commit
      </Button>
    </div>
  );
}

/**
 * La barra delle azioni in blocco sulle bozze.
 *
 * Nasce il 21-08-2026: generate dieci voci in un colpo, approvarle a una a una
 * è il collo di bottiglia che ha fermato il feed a giugno la prima volta.
 *
 * Distingue **pronte** (hanno titolo e corpo — pubblicabili) da **grezze** (solo
 * il commit): «Seleziona le pronte» è la scorciatoia che serve nel 90% dei casi,
 * perché dopo una generazione le pronte sono esattamente quello che vuoi
 * pubblicare.
 */
function BarraSelezione({
  tab,
  voci,
  pronte,
  selezione,
  onSelezione,
}: {
  tab: FeedStato;
  voci: VoceFeedAdmin[];
  pronte: VoceFeedAdmin[];
  selezione: Set<string>;
  onSelezione: (s: Set<string>) => void;
}) {
  const pubblicaMolte = usePubblicaMolte();
  const scartaMolte = useScartaMolte();
  const ripristinaMolte = useRipristinaMolte();
  const [esito, setEsito] = useState<string | null>(null);

  const ids = [...selezione];
  const selezionatePronte = pronte.filter((v) => selezione.has(v.id));
  const isPending =
    pubblicaMolte.isPending || scartaMolte.isPending || ripristinaMolte.isPending;

  function conEsito(r: { fatte: number; saltate: number; motivo_saltate: string | null }, verbo: string) {
    onSelezione(new Set());
    setEsito(
      r.saltate > 0
        ? `${r.fatte} ${verbo}, ${r.saltate} saltate (${r.motivo_saltate ?? "non idonee"}).`
        : `${r.fatte} ${verbo}.`
    );
  }

  return (
    <div className="rounded-lg border bg-muted/30 px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
      {tab === "bozza" && (
        <button
          type="button"
          onClick={() => onSelezione(new Set(pronte.map((v) => v.id)))}
          disabled={pronte.length === 0 || isPending}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <CheckSquare className="size-3.5" />
          Seleziona le pronte ({pronte.length})
        </button>
      )}

      <button
        type="button"
        onClick={() => onSelezione(new Set(voci.map((v) => v.id)))}
        disabled={isPending}
        className="text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
      >
        Tutte ({voci.length})
      </button>

      {selezione.size > 0 && (
        <button
          type="button"
          onClick={() => onSelezione(new Set())}
          disabled={isPending}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Deseleziona
        </button>
      )}

      <span className="flex-1" />

      {esito && <span className="text-xs text-muted-foreground">{esito}</span>}

      {selezione.size > 0 && (
        <>
          <span className="text-xs text-muted-foreground tabular-nums">
            {selezione.size} selezionate
            {tab === "bozza" &&
              selezionatePronte.length !== selezione.size &&
              ` · ${selezionatePronte.length} pubblicabili`}
          </span>

          {/* Su Scartate e Pubblicate l'unica azione sensata è tornare indietro:
              è il ritorno che allo «Scarta» in blocco mancava. */}
          {tab !== "bozza" ? (
            <Button
              size="sm"
              variant="outline"
              disabled={isPending}
              onClick={() => {
                if (
                  tab === "pubblicato" &&
                  !confirm(
                    `Ritirare ${selezione.size} voci dalla vetrina del cliente? ` +
                      "Tornano in bozza. La mail già inviata non si richiama."
                  )
                )
                  return;
                ripristinaMolte.mutate(ids, {
                  onSuccess: (r) => conEsito(r, "riportate in bozza"),
                });
              }}
            >
              {isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Undo2 className="size-3.5" />
              )}
              Riporta in bozza
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline"
                disabled={isPending}
                onClick={() => {
                  if (
                    !confirm(
                      `Scartare ${selezione.size} voci? Restano in DB e si possono ` +
                        "riportare in bozza dalla scheda «Scartate»."
                    )
                  )
                    return;
                  scartaMolte.mutate(ids, { onSuccess: (r) => conEsito(r, "scartate") });
                }}
              >
                <Trash2 className="size-3.5" />
                Scarta
              </Button>
              <Button
                size="sm"
                disabled={isPending || selezionatePronte.length === 0}
                onClick={() => {
                  if (
                    !confirm(
                      `Pubblicare ${selezionatePronte.length} voci? Il cliente riceve UNA mail riepilogativa.`
                    )
                  )
                    return;
                  pubblicaMolte.mutate(ids, { onSuccess: (r) => conEsito(r, "pubblicate") });
                }}
              >
                {isPending ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Send className="size-3.5" />
                )}
                Pubblica {selezionatePronte.length > 0 && `(${selezionatePronte.length})`}
              </Button>
            </>
          )}
        </>
      )}
    </div>
  );
}
